<template>
  <PageHeader
    title="Mes litiges et retours"
    subtitle="Consultez les demandes liées à vos commandes."
    class="mb-4 pr-10"
  />

  <div class="px-3 pb-8 md:px-0">
    <div class="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
      <div class="stat-card"><strong>{{ stats.total }}</strong><span>Total</span></div>
      <div class="stat-card"><strong class="text-red-600">{{ stats.litiges }}</strong><span>Litiges</span></div>
      <div class="stat-card"><strong class="text-amber-600">{{ stats.retours }}</strong><span>Retours</span></div>
      <div class="stat-card"><strong class="text-blue-600">{{ stats.ouverts }}</strong><span>Ouverts</span></div>
    </div>

    <div v-if="isLoading" class="py-12 text-center text-black">
      <i class="fas fa-spinner fa-spin text-2xl"></i>
      <p class="mt-2">Chargement des demandes...</p>
    </div>

    <div
      v-else-if="errorMessage"
      class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
    >
      {{ errorMessage }}
    </div>

    <div
      v-else-if="items.length === 0"
      class="rounded-xl border border-gray-200 bg-white py-12 text-center shadow-sm"
    >
      <i class="fas fa-inbox mb-3 text-4xl text-gray-400"></i>
      <p class="text-black">Aucune demande.</p>
    </div>

    <template v-else>
      <div class="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="table-heading">N° demande</th>
              <th class="table-heading">Commande groupe</th>
              <th class="table-heading">Acheteur</th>
              <th class="table-heading">Type</th>
              <th class="table-heading">Statut</th>
              <th class="table-heading">Date</th>
              <th class="table-heading">Description</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="item in items" :key="item.id" class="hover:bg-gray-50">
              <td class="table-cell font-semibold">#{{ item.id }}</td>
              <td class="table-cell">{{ item.orderGroupId }}</td>
              <td class="table-cell">{{ item.buyerName || '—' }}</td>
              <td class="table-cell">{{ typeLabel(item.typeRequest) }}</td>
              <td class="table-cell">
                <span :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span>
              </td>
              <td class="table-cell">{{ formatDate(item.dateCommande) }}</td>
              <td class="min-w-96 max-w-xl whitespace-normal px-4 py-4 align-top text-sm text-black">
                <p class="leading-6">{{ item.description }}</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <UnifiedPagination
        :current-page="page"
        :total-items="total"
        :items-per-page="limit"
        :items-per-page-options="[10, 25, 50]"
        item-label="demandes"
        @update:current-page="changePage"
        @update:items-per-page="changeLimit"
      />
    </template>
  </div>

</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { PageHeader, UnifiedPagination } from '@/components'
import {
  fetchSellerLitigesRetours,
  fetchSellerLitigeRetourStats,
  type LitigeRetourStats,
  type LitigeRetourStatus,
  type LitigeRetourType,
  type SellerLitigeRetour,
} from '@/api/litigesRetours'

const items = ref<SellerLitigeRetour[]>([])
const total = ref(0)
const page = ref(1)
const limit = ref(10)
const isLoading = ref(false)
const errorMessage = ref('')
const stats = ref<LitigeRetourStats>({
  total: 0,
  ouverts: 0,
  litiges: 0,
  retours: 0,
})

async function loadItems(): Promise<void> {
  isLoading.value = true
  errorMessage.value = ''
  try {
    const [result, statsResult] = await Promise.all([
      fetchSellerLitigesRetours({ page: page.value, limit: limit.value }),
      fetchSellerLitigeRetourStats(),
    ])
    items.value = result.items
    total.value = result.total
    stats.value = statsResult
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Impossible de charger les demandes.'
  } finally {
    isLoading.value = false
  }
}

function changePage(value: number): void {
  page.value = value
  void loadItems()
}

function changeLimit(value: number): void {
  page.value = 1
  limit.value = value
  void loadItems()
}

function typeLabel(type: LitigeRetourType): string {
  return type === 'litige' ? 'Litige' : 'Retour'
}

function statusLabel(status: LitigeRetourStatus): string {
  return {
    en_attente: 'En attente',
    en_traitement: 'En traitement',
    resolu: 'Résolu',
    rejete: 'Rejeté',
  }[status]
}

function statusClass(status: LitigeRetourStatus): string[] {
  return [
    'inline-flex rounded-full px-2.5 py-1 text-xs font-semibold',
    {
      en_attente: 'bg-amber-100 text-amber-700',
      en_traitement: 'bg-blue-100 text-blue-700',
      resolu: 'bg-green-100 text-green-700',
      rejete: 'bg-red-100 text-red-700',
    }[status],
  ]
}

function formatDate(value: string | null): string {
  if (!value || value.startsWith('0000-00-00')) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('fr-FR').format(date)
}

onMounted(loadItems)
</script>

<style scoped>
.table-heading {
  @apply whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600;
}

.table-cell {
  @apply whitespace-nowrap px-4 py-3 text-sm text-black;
}

.stat-card {
  @apply flex min-h-24 flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-3 text-center shadow-sm;
}

.stat-card strong {
  @apply text-2xl;
}

.stat-card span {
  @apply mt-1 text-xs text-gray-500;
}
</style>
